import React, { useState } from 'react';
import { X, RefreshCcw, Copy } from 'lucide-react';
import { Rocket } from 'lucide-react';

interface IntegrationModalProps {
  onClose: () => void;
}

const DeploymentModal: React.FC<IntegrationModalProps> = ({ onClose }) => {
  const [deploymentType, setDeploymentType] = useState('Chatbot');
  const [workflow, setWorkflow] = useState('');
  const [domain, setDomain] = useState('');
  const [apiKey, setApiKey] = useState('sk-ABCD1234XYZ');
  const [token] = useState('token-XYZ-5678');
  const [status, setStatus] = useState('Active');

  const workflows = ['Task Manager Bot', 'Invoice Reporter', 'Lead Nurturing Agent'];

  const regenerateApiKey = () => {
    setApiKey(`sk-${Math.random().toString(36).substring(2, 10).toUpperCase()}`);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const renderDynamicFields = () => {
    const commonFields = (
      <>
        <label className="block text-gray-400 mt-4 text-sm">Domain</label>
        <input
          type="text"
          placeholder="e.g., app.clientdomain.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          className="w-full p-2 rounded bg-neutral-800 text-white mt-1 text-sm"
        />

       
        <label className="block text-gray-400 mt-4 text-sm">Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-full p-2 rounded bg-neutral-800 text-white mt-1 text-sm"
        >
          <option>Active</option>
          <option>Inactive</option>
        </select>
      </>
    );

    switch (deploymentType) {
      case 'Chatbot':
        return (
          <>
            {commonFields}

            <label className="block text-gray-400 mt-4 text-sm">API Key</label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                readOnly
                value={apiKey}
                className="w-full p-2 rounded bg-neutral-800 text-green-400 mt-1 text-sm"
              />
              <button onClick={regenerateApiKey} className="p-2 bg-neutral-700 hover:bg-neutral-600 rounded">
                <RefreshCcw className="w-4 h-4 text-gray-300" />
              </button>
            </div>

            <label className="block text-gray-400 mt-4 text-sm">Select Workflow</label>
            <select
              value={workflow}
              onChange={(e) => setWorkflow(e.target.value)}
              className="w-full p-2 rounded bg-neutral-800 text-white mt-1 text-sm"
            >
              <option value="">Select Workflow</option>
              {workflows.map((wf) => (
                <option key={wf} value={wf}>{wf}</option>
              ))}
            </select>

            {workflow && domain && (
              <>
                <label className="block text-gray-400 mt-4 text-sm">Generated Embed Code</label>
                <div className="relative">
                  <textarea
                    rows={4}
                    readOnly
                    value={`<iframe src="https://${domain}/chatbot?workflow=${encodeURIComponent(workflow)}&apiKey=${apiKey}" width="100%" height="500px"></iframe>`}
                    className="w-full p-3 rounded bg-neutral-900 text-green-400 text-xs"
                  />
                  <button 
                    onClick={() => copyToClipboard(`<iframe src="https://${domain}/chatbot?workflow=${encodeURIComponent(workflow)}&apiKey=${apiKey}" width="100%" height="500px"></iframe>`)} 
                    className="absolute top-2 right-2 p-1 bg-neutral-700 hover:bg-neutral-600 rounded"
                  >
                    <Copy className="w-4 h-4 text-gray-300" />
                  </button>
                </div>
              </>
            )}
          </>
        );

      case 'API Endpoint':
      case 'Webhook':
        const isApi = deploymentType === 'API Endpoint';
        const generatedUrl = isApi
          ? `POST https://${domain}/api/v1/workflows/trigger\n\nHeaders:\nAuthorization: Bearer ${token}\n\nBody:\n{\n  "workflow": "${workflow}"\n}`
          : `https://${domain}/webhook/trigger?token=${token}`;

        return (
          <>
            {commonFields}

            {domain && (
              <>
                <label className="block text-gray-400 mt-4 text-sm">
                  {isApi ? 'Generated API Endpoint' : 'Generated Webhook URL'}
                </label>
                <div className="relative">
                  <textarea
                    rows={isApi ? 6 : 3}
                    readOnly
                    value={generatedUrl}
                    className="w-full p-3 rounded bg-neutral-900 text-yellow-400 text-xs"
                  />
                  <button 
                    onClick={() => copyToClipboard(generatedUrl)} 
                    className="absolute top-2 right-2 p-1 bg-neutral-700 hover:bg-neutral-600 rounded"
                  >
                    <Copy className="w-4 h-4 text-gray-300" />
                  </button>
                </div>
              </>
            )}
          </>
        );

      case 'Reporting':
        return (
          <p className="text-gray-400 mt-4 text-sm">
            Reporting links will be shared via email or chat notifications configured in the system workflows.
          </p>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-neutral-900 p-8 rounded-xl shadow-2xl w-full max-w-lg text-white relative">
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-200"
        >
          <X className="w-5 h-5" />
        </button>

<h4 className="text-x font-semibold mb-4 text-center text-gray-200 flex items-center justify-center gap-2">
  <Rocket className="w-5 h-5 text-indigo-400" />
  Create New Deployment
</h4>

        <label className="block text-gray-400 text-sm">Deployment Type</label>
        <select
          value={deploymentType}
          onChange={(e) => setDeploymentType(e.target.value)}
          className="w-full p-2 rounded bg-neutral-800 text-white mt-1 text-sm"
        >
          <option>Chatbot</option>
          <option>API Endpoint</option>
          <option>Reporting</option>
          <option>Webhook</option>
        </select>

        {renderDynamicFields()}

        <div className="mt-8 text-right">
          <button
            onClick={onClose}
            className="bg-indigo-600 hover:bg-indigo-700 px-5 py-2 rounded-md text-white text-sm font-medium shadow-lg"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeploymentModal;
